<template>
  <div class="invoices-tab">
    <div class="section-header">
      <div>
        <h2>Invoices</h2>
        <p>Create and manage invoices for your services</p>
      </div>
      <b-button type="is-primary" @click="openCreateModal">
        <b-icon icon="plus" size="is-small"></b-icon>
        <span>Create Invoice</span>
      </b-button>
    </div>

    <div class="loading-state" v-if="loading">
      <b-loading :active="true" :is-full-page="false"></b-loading>
    </div>

    <div class="empty-state" v-else-if="!invoices.length">
      <b-icon icon="file-document" size="is-large"></b-icon>
      <h3>No invoices yet</h3>
      <p>Create your first invoice to bill for your services.</p>
      <b-button type="is-primary" @click="openCreateModal">Create Invoice</b-button>
    </div>

    <div class="invoices-list" v-else>
      <div class="invoice-card" v-for="invoice in invoices" :key="invoice.id">
        <div class="invoice-info">
          <h4>{{ invoice.invoice_number }}</h4>
          <p class="bill-to">Bill to: {{ invoice.bill_to_name }}</p>
          <p class="invoice-date">{{ formatDate(invoice.created_at) }}</p>
        </div>
        <div class="invoice-amount">
          <span class="amount">{{ formatAmount(invoice.total) }}</span>
          <span class="currency">{{ invoice.currency }}</span>
        </div>
        <div class="invoice-status">
          <span class="status-badge" :class="invoice.status">{{ invoice.status }}</span>
        </div>
        <div class="invoice-actions">
          <b-button type="is-info" size="is-small" outlined @click="startEdit(invoice)" :loading="loadingEdit && editingInvoiceId === invoice.id">
            <b-icon icon="pencil" size="is-small"></b-icon>
            <span>Edit</span>
          </b-button>
          <b-button type="is-primary" size="is-small" outlined @click="downloadInvoice(invoice)">
            <b-icon icon="download" size="is-small"></b-icon>
            <span>Download</span>
          </b-button>
          <b-button type="is-danger" size="is-small" outlined @click="confirmDelete(invoice)">
            <b-icon icon="delete" size="is-small"></b-icon>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Invoice Form Modal -->
    <transition name="fade">
      <div class="invoice-modal-overlay" v-if="showModal" @click.self="cancelForm">
        <div class="invoice-modal">
          <div class="modal-header">
            <div class="modal-title">
              <h2>{{ isEditing ? 'Edit Invoice' : 'New Invoice' }}</h2>
              <span v-if="isEditing && editingInvoiceNumber" class="invoice-ref">{{ editingInvoiceNumber }}</span>
            </div>
            <button class="modal-close" @click="cancelForm" aria-label="Close">
              <b-icon icon="close"></b-icon>
            </button>
          </div>

          <div class="modal-body">
            <div class="loading-widget" v-if="loadingEdit">
              <b-loading :active="true" :is-full-page="false"></b-loading>
            </div>

            <template v-else>
              <div class="form-section">
                <h4>Bill To</h4>
                <div class="form-grid">
                  <b-field label="Name *">
                    <b-input v-model="form.bill_to_name" placeholder="Client or company name"></b-input>
                  </b-field>
                  <b-field label="Email">
                    <b-input v-model="form.bill_to_email" type="email" placeholder="client@example.com"></b-input>
                  </b-field>
                  <b-field label="Phone">
                    <b-input v-model="form.bill_to_phone" placeholder="+250 ..."></b-input>
                  </b-field>
                  <b-field label="Address" class="full-width">
                    <b-input v-model="form.bill_to_address" placeholder="Full address"></b-input>
                  </b-field>
                </div>
              </div>

              <div class="form-section">
                <h4>Invoice Items</h4>
                <div class="items-list">
                  <div class="item-row" v-for="(item, index) in form.items" :key="'invoice-item-' + index">
                    <b-field label="Description" class="item-desc">
                      <b-input v-model="item.description" placeholder="Item description"></b-input>
                    </b-field>
                    <b-field label="Qty">
                      <b-input v-model.number="item.quantity" type="number" min="1" step="1" placeholder="1"></b-input>
                    </b-field>
                    <b-field label="Unit Price">
                      <b-input v-model.number="item.unit_price" type="number" min="0" step="1" placeholder="0"></b-input>
                    </b-field>
                    <div class="item-total">
                      <label>Total</label>
                      <span>{{ formatAmount(getItemTotal(item)) }}</span>
                    </div>
                    <button class="remove-item" @click="removeItem(index)" v-if="form.items.length > 1">
                      <b-icon icon="close" size="is-small"></b-icon>
                    </button>
                  </div>
                </div>
                <b-button type="is-light" size="is-small" @click="addItem">
                  <b-icon icon="plus" size="is-small"></b-icon> Add Item
                </b-button>
              </div>

              <div class="form-section">
                <h4>Payment Information</h4>
                <div class="form-grid">
                  <b-field label="Payment Method">
                    <b-select v-model="form.payment_method" expanded>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="mobile_money">Mobile Money</option>
                      <option value="cash">Cash</option>
                      <option value="other">Other</option>
                    </b-select>
                  </b-field>
                  <b-field label="Status">
                    <b-select v-model="form.status" expanded>
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                    </b-select>
                  </b-field>
                  <b-field label="Account Name">
                    <b-input v-model="form.payment_account_name" placeholder="Account holder name"></b-input>
                  </b-field>
                  <b-field label="Account Number">
                    <b-input v-model="form.payment_account_number" placeholder="Account or phone number"></b-input>
                  </b-field>
                  <b-field label="Bank Name">
                    <b-input v-model="form.payment_bank_name" placeholder="Bank or provider name"></b-input>
                  </b-field>
                  <b-field label="Tax Rate (%)">
                    <b-input v-model.number="form.tax_rate" type="number" min="0" max="100" step="1" placeholder="0"></b-input>
                  </b-field>
                  <b-field label="Due Date">
                    <b-datepicker v-model="form.due_date" placeholder="Select due date" icon="calendar-today"></b-datepicker>
                  </b-field>
                  <b-field label="Payment Notes" class="full-width">
                    <b-input v-model="form.payment_notes" type="textarea" placeholder="Additional payment instructions"></b-input>
                  </b-field>
                  <b-field label="Notes" class="full-width">
                    <b-input v-model="form.notes" type="textarea" placeholder="Invoice notes"></b-input>
                  </b-field>
                </div>
              </div>

              <div class="form-summary">
                <div class="summary-row"><span>Subtotal</span><span>{{ formatAmount(subtotal) }} RWF</span></div>
                <div class="summary-row" v-if="form.tax_rate > 0"><span>Tax ({{ form.tax_rate }}%)</span><span>{{ formatAmount(taxAmount) }} RWF</span></div>
                <div class="summary-row total"><span>Total</span><span>{{ formatAmount(total) }} RWF</span></div>
              </div>
            </template>
          </div>

          <div class="modal-footer">
            <b-button type="is-primary" :loading="saving" :disabled="loadingEdit" @click="saveInvoice">
              {{ isEditing ? 'Update Invoice' : 'Create Invoice' }}
            </b-button>
            <b-button @click="cancelForm" :disabled="saving">Cancel</b-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { BASE_URL } from '../../../store/variables'

export default {
  name: 'InvoicesTab',
  data() {
    return {
      loading: true,
      loadingEdit: false,
      saving: false,
      showModal: false,
      editingInvoiceId: null,
      editingInvoiceNumber: '',
      invoices: [],
      form: this.getDefaultForm(),
    }
  },
  computed: {
    isEditing() {
      return this.editingInvoiceId !== null
    },
    subtotal() {
      return this.form.items.reduce((sum, item) => sum + this.getItemTotal(item), 0)
    },
    taxAmount() {
      return this.subtotal * (this.form.tax_rate / 100)
    },
    total() {
      return this.subtotal + this.taxAmount
    },
  },
  mounted() {
    this.loadInvoices()
  },
  methods: {
    getDefaultForm() {
      return {
        bill_to_name: '',
        bill_to_email: '',
        bill_to_phone: '',
        bill_to_address: '',
        payment_method: 'bank_transfer',
        payment_account_name: '',
        payment_account_number: '',
        payment_bank_name: '',
        payment_notes: '',
        notes: '',
        tax_rate: 0,
        currency: 'RWF',
        status: 'draft',
        due_date: null,
        items: [{ description: '', quantity: 1, unit_price: 0 }],
      }
    },
    openCreateModal() {
      this.editingInvoiceId = null
      this.editingInvoiceNumber = ''
      this.resetForm()
      this.showModal = true
    },
    cancelForm() {
      if (this.saving) return
      this.showModal = false
      this.editingInvoiceId = null
      this.editingInvoiceNumber = ''
      this.resetForm()
    },
    async loadInvoices() {
      try {
        this.loading = true
        const response = await this.$store.dispatch('PARTICIPANT_GET_INVOICES')
        if (response.data.status === 'ok') {
          this.invoices = response.data.data || []
        }
      } catch (error) {
        console.error('Failed to load invoices:', error)
      } finally {
        this.loading = false
      }
    },
    async startEdit(invoice) {
      this.showModal = true
      this.editingInvoiceId = invoice.id
      this.editingInvoiceNumber = invoice.invoice_number

      try {
        this.loadingEdit = true
        const response = await this.$store.dispatch('PARTICIPANT_GET_INVOICE', { invoice_id: invoice.id })
        if (response.data.status !== 'ok') {
          this.notify('Failed to load invoice details', 'error')
          this.cancelForm()
          return
        }

        const data = response.data.data
        this.editingInvoiceId = data.id
        this.editingInvoiceNumber = data.invoice_number
        this.form = {
          bill_to_name: data.bill_to_name || '',
          bill_to_email: data.bill_to_email || '',
          bill_to_phone: data.bill_to_phone || '',
          bill_to_address: data.bill_to_address || '',
          payment_method: data.payment_method || 'bank_transfer',
          payment_account_name: data.payment_account_name || '',
          payment_account_number: data.payment_account_number || '',
          payment_bank_name: data.payment_bank_name || '',
          payment_notes: data.payment_notes || '',
          notes: data.notes || '',
          tax_rate: Number(data.tax_rate) || 0,
          currency: data.currency || 'RWF',
          status: data.status || 'draft',
          due_date: data.due_date ? new Date(data.due_date) : null,
          items: (data.items && data.items.length)
            ? data.items.map(item => ({
              description: item.description || '',
              quantity: Number(item.quantity) || 1,
              unit_price: Number(item.unit_price) || 0,
            }))
            : [{ description: '', quantity: 1, unit_price: 0 }],
        }
      } catch (error) {
        this.notify('Failed to load invoice for editing', 'error')
        this.cancelForm()
      } finally {
        this.loadingEdit = false
      }
    },
    addItem() {
      this.form.items.push({ description: '', quantity: 1, unit_price: 0 })
    },
    removeItem(index) {
      this.form.items.splice(index, 1)
    },
    getItemTotal(item) {
      const quantity = Number(item.quantity) || 0
      const unitPrice = Number(item.unit_price) || 0
      return quantity * unitPrice
    },
    resetForm() {
      this.form = this.getDefaultForm()
    },
    buildPayload() {
      const validItems = this.form.items
        .map(item => ({
          description: (item.description || '').trim(),
          quantity: Number(item.quantity) || 0,
          unit_price: Number(item.unit_price) || 0,
        }))
        .filter(i => i.description && i.quantity > 0)

      return {
        bill_to_name: this.form.bill_to_name.trim(),
        bill_to_email: this.form.bill_to_email,
        bill_to_phone: this.form.bill_to_phone,
        bill_to_address: this.form.bill_to_address,
        payment_method: this.form.payment_method,
        payment_account_name: this.form.payment_account_name,
        payment_account_number: this.form.payment_account_number,
        payment_bank_name: this.form.payment_bank_name,
        payment_notes: this.form.payment_notes,
        notes: this.form.notes,
        tax_rate: Number(this.form.tax_rate) || 0,
        currency: this.form.currency,
        status: this.form.status,
        due_date: this.form.due_date ? this.formatDateForApi(this.form.due_date) : null,
        items: validItems,
      }
    },
    async saveInvoice() {
      if (!this.form.bill_to_name.trim()) {
        this.notify('Bill to name is required', 'warn')
        return
      }

      const payload = this.buildPayload()
      if (!payload.items.length) {
        this.notify('Add at least one item with description and quantity', 'warn')
        return
      }

      this.saving = true
      try {
        let response
        if (this.isEditing) {
          response = await this.$store.dispatch('PARTICIPANT_UPDATE_INVOICE', {
            invoice_id: this.editingInvoiceId,
            ...payload,
          })
        } else {
          response = await this.$store.dispatch('PARTICIPANT_CREATE_INVOICE', payload)
        }

        if (response.data.status === 'ok') {
          this.notify(this.isEditing ? 'Invoice updated successfully' : 'Invoice created successfully', 'success')
          this.cancelForm()
          this.loadInvoices()
        } else {
          this.notify(response.data.message || 'Failed to save invoice', 'error')
        }
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message) || 'Failed to save invoice'
        this.notify(msg, 'error')
      } finally {
        this.saving = false
      }
    },
    notify(text, type) {
      this.$notify({
        group: 'status',
        type: type === 'success' ? 'success' : type === 'warn' ? 'warn' : 'error',
        title: type === 'success' ? 'Success' : type === 'warn' ? 'Warning' : 'Error',
        text,
      })
    },
    downloadInvoice(invoice) {
      const token = this.$store.getters.getUserAccessToken
      const url = `${BASE_URL}template/generate-participant-invoice?invoice_id=${invoice.id}&token=${token}`
      window.open(url, '_blank')
    },
    confirmDelete(invoice) {
      this.$buefy.dialog.confirm({
        title: 'Delete Invoice',
        message: `Are you sure you want to delete invoice ${invoice.invoice_number}?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteInvoice(invoice),
      })
    },
    async deleteInvoice(invoice) {
      try {
        if (this.editingInvoiceId === invoice.id) {
          this.cancelForm()
        }
        await this.$store.dispatch('PARTICIPANT_DELETE_INVOICE', { invoice_id: invoice.id })
        this.notify('Invoice deleted', 'success')
        this.loadInvoices()
      } catch (error) {
        this.notify('Failed to delete invoice', 'error')
      }
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatDateForApi(date) {
      const d = new Date(date)
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
  },
}
</script>

<style lang="scss" scoped>
.invoices-tab {
  .section-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 16px; flex-wrap: wrap; gap: 15px;
    h2 { margin: 0; font-size: 20px; color: #1a191b; font-weight: 600; }
    p { margin: 5px 0 0; color: #797979; font-size: 14px; }
  }

  .loading-state { min-height: 200px; position: relative; }

  .empty-state {
    background: #fff; border-radius: 4px; padding: 48px 24px;
    text-align: center; border: 1px solid #ddd;
    .icon { color: #ccc; margin-bottom: 15px; }
    h3 { margin: 0 0 8px; color: #555; }
    p { color: #888; font-size: 14px; margin-bottom: 20px; }
  }

  .invoices-list {
    display: flex; flex-direction: column; gap: 12px;
    .invoice-card {
      background: #fff; border-radius: 4px; padding: 16px 20px;
      border: 1px solid #ddd;
      display: grid; grid-template-columns: 1fr auto auto auto; gap: 16px; align-items: center;

      .invoice-info {
        h4 { margin: 0 0 4px; font-size: 15px; color: #1a191b; font-weight: 600; }
        .bill-to { margin: 0; font-size: 13px; color: #797979; }
        .invoice-date { margin: 4px 0 0; font-size: 12px; color: #aaa; }
      }
      .invoice-amount {
        text-align: right;
        .amount { display: block; font-size: 18px; font-weight: 700; color: #1a191b; }
        .currency { font-size: 12px; color: #797979; }
      }
      .status-badge {
        display: inline-block; padding: 4px 12px; border-radius: 20px;
        font-size: 11px; font-weight: 600; text-transform: uppercase;
        &.draft { background: #fff3e0; color: #e65100; }
        &.sent { background: #e3f2fd; color: #1565c0; }
        &.paid { background: #e8f5e9; color: #2e7d32; }
      }
      .invoice-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    }
  }
}

.invoice-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.65);
}

.invoice-modal {
  width: 100%;
  max-width: 860px;
  max-height: calc(100vh - 48px);
  background: #fff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 24px;
    border-bottom: 1px solid #ddd;
    background: #fff;
    color: #1a191b;

    .modal-title {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;

      h2 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
      }

      .invoice-ref {
        font-size: 12px;
        background: #fafafa;
        border: 1px solid #ddd;
        padding: 4px 10px;
        border-radius: 4px;
        color: #797979;
      }
    }

    .modal-close {
      background: rgba(255, 255, 255, 0.15);
      border: none;
      color: #fff;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      &:hover { background: rgba(255, 255, 255, 0.25); }
    }
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    position: relative;

    .loading-widget {
      min-height: 240px;
      position: relative;
    }

    .form-section {
      margin-bottom: 24px;
      padding-bottom: 20px;
      border-bottom: 1px solid #eee;

      &:last-of-type { border-bottom: none; }

      h4 {
        margin: 0 0 15px;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #888;
      }
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 15px;

      .full-width { grid-column: 1 / -1; }
    }

    .items-list {
      margin-bottom: 15px;

      .item-row {
        display: grid;
        grid-template-columns: minmax(180px, 2fr) minmax(90px, 120px) minmax(120px, 160px) minmax(90px, 120px) 36px;
        gap: 12px;
        align-items: end;
        margin-bottom: 12px;

        ::v-deep .input { width: 100%; }

        .item-total {
          label { display: block; font-size: 12px; color: #888; margin-bottom: 4px; }
          span { font-weight: 600; color: #333; font-size: 14px; }
        }

        .remove-item {
          background: #fee;
          border: none;
          border-radius: 6px;
          width: 36px;
          height: 36px;
          cursor: pointer;
          color: #dc3545;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover { background: #fdd; }
        }
      }
    }

    .form-summary {
      background: #f8f9fb;
      border-radius: 8px;
      padding: 20px;
      max-width: 320px;
      margin-left: auto;

      .summary-row {
        display: flex;
        justify-content: space-between;
        padding: 6px 0;
        font-size: 14px;
        color: #555;

        &.total {
          border-top: 2px solid #333;
          margin-top: 8px;
          padding-top: 12px;
          font-size: 18px;
          font-weight: 700;
          color: #222;
        }
      }
    }
  }

  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #eee;
    background: #fafbfc;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .invoices-tab .invoices-list .invoice-card {
    grid-template-columns: 1fr;
    text-align: center;

    .invoice-amount { text-align: center; }
    .invoice-actions { justify-content: center; }
  }

  .invoice-modal {
    max-height: calc(100vh - 32px);

    .modal-body {
      padding: 16px;

      .form-grid { grid-template-columns: 1fr; }
      .items-list .item-row { grid-template-columns: 1fr; }
    }

    .modal-footer {
      flex-direction: column-reverse;

      .button { width: 100%; }
    }
  }
}
</style>
